import { debounce } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const usePreviousValue = (value) => {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};

/**
 * Hook that tracks previous value and triggers re-render when it changes
 */
export const usePreviousValueState = (value) => {
  const [prevValue, setPrevValue] = useState(value);

  useEffect(() => {
    setPrevValue(value);
  }, [value]);

  return prevValue;
};

/**
 * usePolling — calls an API over and over until it gets the answer you want,
 * or until time runs out.
 *
 * ## The short version
 *
 * ```js
 * const { startPolling, isPolling } = usePolling({
 *   pollFn: () => refetchApplication().unwrap(), // what to call each time
 *   interval: 10000,                             // wait 10s between calls
 *   totalPollingTime: 60000,                     // give up after 1 minute
 *   stopCondition: (data) => Boolean(data?.umrn), // "got it, stop"
 *   onComplete: (data, err, { resetPolling }) => {
 *     resetPolling();
 *     if (data?.umrn) return;      // success
 *     showTimedOutModal();         // ran out of time
 *   },
 * });
 *
 * // nothing happens until you call this (unless immediate: true)
 * startPolling();
 * ```
 *
 * ## How it decides to keep going
 *
 * 1. Calls `pollFn`.
 * 2. Passes the result to `stopCondition`. If it returns true → stop, call `onComplete`.
 * 3. If the attempt budget is used up → stop, call `onComplete` with
 *    `maxAttemptsReached: true`.
 * 4. Otherwise waits `interval` ms and goes back to step 1.
 *
 * The budget is `totalPollingTime / interval + 1`. So 60000 / 10000 + 1 = 7 calls.
 * You can pass `maxAttempts` instead if you'd rather count calls than seconds.
 *
 * ## Options
 *
 * - `pollFn` — the function to call. Can be an `async` function, an RTK Query
 *   `refetch().unwrap()`, or an RTK trigger. If it resolves to `{ data }` or
 *   `{ error }` (the raw RTK shape) the hook unwraps it for you.
 * - `interval` — milliseconds between calls. Required.
 * - `totalPollingTime` — total milliseconds to keep trying. Converted to a call count.
 * - `maxAttempts` — alternative to `totalPollingTime`; a flat number of calls.
 * - `stopCondition(data)` — return `true` when you have what you wanted.
 *   Leave it out to always run the full budget.
 * - `immediate` — `true` starts polling on mount. Default `false`.
 * - `shouldPoll` — set to `false` to stop/prevent polling. Default `true`.
 * - `retryOnError` — keep polling after a failed call. Default `true`.
 * - `retryDelay` — wait time after a failed call. Defaults to `interval`.
 * - `onStart` / `onSuccess(data)` / `onError(err)` — per-event callbacks.
 * - `onComplete(data, error, { maxAttemptsReached, resetPolling })` — fires once
 *   when polling stops, for any reason. This is where most logic goes.
 *
 * Callbacks are read from refs, so inline arrow functions are safe — they will
 * not restart polling or cause render loops.
 *
 * ## What you get back
 *
 * - `startPolling()` — begin. Ignored if already polling.
 * - `stopPolling()` — stop now, without firing `onComplete`.
 * - `resetPolling()` — stop and clear `data` / `error` / attempt count. Call this
 *   inside `onComplete` so the next `startPolling()` starts clean.
 * - `data`, `error`, `isSuccess`, `isError` — from the latest call.
 * - `isLoading` — the first call is in flight.
 * - `isFetching` — any call is in flight.
 * - `isPolling` — a polling run is active. Handy for a loader:
 *   `<Loader open={isPolling} />`.
 * - `attempt`, `totalMaxAttempts` — progress, e.g. "attempt 3 of 7".
 *
 * ## Things that trip people up
 *
 * - `onComplete` runs on success *and* on timeout. Check `maxAttemptsReached`,
 *   or re-check your condition against `data`, to tell them apart.
 * - `interval` and `totalPollingTime` are captured when a run starts. To poll for
 *   a different duration (e.g. a longer retry pass), create a second `usePolling`
 *   instance rather than changing the value mid-run.
 * - `startPolling()` does nothing while a run is active. Call `resetPolling()`
 *   first if you need to restart early.
 * - Need two APIs per tick? Do it inside `pollFn` with `Promise.all` and return
 *   the one value `stopCondition` should look at.
 */
export function usePolling({
  pollFn,
  interval,
  maxAttempts,
  totalPollingTime, // Alternative to maxAttempts: stop after this many seconds
  shouldPoll = true,
  stopCondition,
  onStart,
  onSuccess,
  onError,
  onComplete,
  immediate = false,
  retryOnError = true,
  retryDelay,
}) {
  const totalMaxAttempts = totalPollingTime
    ? Math.ceil(totalPollingTime / interval) + 1
    : maxAttempts; //  max attempts based on totalPollingTime and interval if totalPollingTime is provided (+1 for initial attempt), otherwise use maxAttempts.
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  const timeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const attemptRef = useRef(0);
  const isPollingRef = useRef(false);

  // Store callbacks in refs so users can pass inline arrow functions
  // without causing infinite re-renders
  const pollFnRef = useRef(pollFn);
  const stopConditionRef = useRef(stopCondition);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onCompleteRef = useRef(onComplete);
  const onStartRef = useRef(onStart);

  // Keep refs updated with latest callbacks
  useEffect(() => {
    pollFnRef.current = pollFn;
    stopConditionRef.current = stopCondition;
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    onCompleteRef.current = onComplete;
    onStartRef.current = onStart;
  });

  const clearTimeoutRef = useCallback(() => {
    if (timeoutRef.current) {
      console.log("clearing timeout", timeoutRef.current);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const stopInternal = useCallback(() => {
    clearTimeoutRef();
    isPollingRef.current = false;
    if (isMountedRef.current) {
      setIsPolling(false);
      setIsFetching(false);
    }
  }, [clearTimeoutRef]);

  const resetPolling = useCallback(() => {
    stopInternal();
    attemptRef.current = 0;

    setData(null);
    setError(null);
    setAttempt(0);
    setIsLoading(false);
    setIsFetching(false);
    setIsPolling(false);
    setIsSuccess(false);
    setIsError(false);
  }, [stopInternal]);

  const executePoll = useCallback(async () => {
    if (!isMountedRef.current || !isPollingRef.current) return;

    const currentAttempt = attemptRef.current + 1;
    attemptRef.current = currentAttempt;

    if (isMountedRef.current) {
      setAttempt(currentAttempt);
      setIsFetching(true);
      if (currentAttempt === 1) {
        setIsLoading(true);
      }
    }

    // Helper to handle error state
    const handleError = (err) => {
      if (!isMountedRef.current) return;

      setError(err);
      setIsError(true);
      setIsSuccess(false);
      setIsFetching(false);
      setIsLoading(false);

      onErrorRef.current?.(err);

      const reachedMax = totalMaxAttempts && currentAttempt >= totalMaxAttempts;

      if (reachedMax) {
        stopInternal();
        onCompleteRef.current?.(null, err, {
          maxAttemptsReached: reachedMax,
          resetPolling,
        });
        return;
      }

      if (retryOnError && isPollingRef.current && isMountedRef.current) {
        const delay = retryDelay ?? interval;
        timeoutRef.current = setTimeout(executePoll, delay);
      } else {
        stopInternal();
        onCompleteRef.current?.(null, err, {
          maxAttemptsReached: reachedMax,
          resetPolling,
        });
      }
    };

    // Helper to handle success state
    const handleSuccess = (responseData) => {
      if (!isMountedRef.current) return;

      setData(responseData);
      setError(null);
      setIsSuccess(true);
      setIsError(false);
      setIsFetching(false);
      setIsLoading(false);

      onSuccessRef.current?.(responseData);

      const shouldStop = stopConditionRef.current?.(responseData);
      const reachedMax = totalMaxAttempts && currentAttempt >= totalMaxAttempts;

      if (shouldStop || reachedMax) {
        stopInternal();
        onCompleteRef.current?.(responseData, null, {
          maxAttemptsReached: reachedMax,
          resetPolling,
        });
        return;
      }

      if (isPollingRef.current && isMountedRef.current) {
        timeoutRef.current = setTimeout(executePoll, interval);
      }
    };

    try {
      const result = await pollFnRef.current();

      if (!isMountedRef.current) return;

      // Handle RTK Query style response: { data } or { error }
      // RTK Query always resolves, never rejects
      const isRTKResponse =
        result &&
        typeof result === "object" &&
        ("data" in result || "error" in result);

      if (isRTKResponse) {
        if (result.error !== undefined) {
          handleError(result.error);
        } else {
          handleSuccess(result.data);
        }
      } else {
        // Regular promise (fetch, axios, etc.) - result is the data directly
        handleSuccess(result);
      }
    } catch (err) {
      // Catch block for fetch/axios errors (they reject on error)
      handleError(err);
    }
  }, [
    interval,
    retryOnError,
    retryDelay,
    stopInternal,
    totalPollingTime,
    resetPolling,
  ]);

  const startPolling = useCallback(() => {
    if (isPollingRef.current) return;

    attemptRef.current = 0;
    isPollingRef.current = true;

    setAttempt(0);
    setIsPolling(true);
    setIsLoading(true);
    setError(null);
    setIsError(false);
    setIsSuccess(false);

    onStartRef.current?.();
    executePoll();
  }, [executePoll]);

  const stopPolling = useCallback(() => {
    stopInternal();
  }, [stopInternal]);

  useEffect(() => {
    isMountedRef.current = true;

    if (shouldPoll && immediate) {
      startPolling();
    }

    // return () => {
    //   isMountedRef.current = false;
    //   clearTimeoutRef();
    // };
  }, [shouldPoll, immediate]);

  useEffect(() => {
    if (!shouldPoll) {
      stopInternal();
    }
  }, [shouldPoll, stopInternal]);

  return {
    startPolling,
    stopPolling,
    resetPolling,
    data,
    error,
    isLoading,
    isFetching,
    isPolling,
    isSuccess,
    isError,
    attempt,
    totalMaxAttempts,
  };
}

export const useDebouncedCallback = (func, delay = 500) => {
  const funcRef = useRef(null);
  funcRef.current = func;

  const debouncedFunc = useMemo(() => {
    const fn = debounce((...args) => {
      funcRef.current?.(...args);
    }, delay);

    return fn;
  }, [delay]);

  useEffect(() => {
    return debouncedFunc.clear;
  }, [debouncedFunc]);

  return debouncedFunc;
};

export const useGeolocation = () => {
  const [isFetching, setIsFetching] = useState(false);
  const [coordinates, setCoordinates] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setIsFetching(true);

    navigator.geolocation.getCurrentPosition(
      function (position) {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        setCoordinates({ lat, lng });
        setIsFetching(false);
      },
      function (error) {
        console.error(error);
        setError(error.message || "Unable to retrieve location");
        setIsFetching(false);
      },
      {
        enableHighAccuracy: true,
      },
    );
  }, []);

  return { coordinates, isFetching, error };
};
